import { useState } from 'react';
import { Maximize2 } from 'lucide-react';

import { cn } from '@shared/utils/cn';

import { MediaPreview } from './media-preview';
import { MediaViewerDialog } from './media-viewer-dialog';

type Props = {
  /** Image or PDF URLs. */
  medias: string[];
  /** Title of the full-size dialog (what the visuals belong to). */
  title: string;
  /** Side of a thumbnail, in px. */
  size?: number;
  className?: string;
};

const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * Thumbnails of visuals (images or PDFs) to check the rendering; a click opens them at full size in a dialog.
 * Clicks and keys stay inside: the thumbnails often sit in a clickable card (a checkbox row…), which must not react.
 */
export const MediaThumbnails = ({ medias, title, size = 96, className }: Props) => {
  const [index, setIndex] = useState<number | null>(null);

  if (medias.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)} onClick={stop} onKeyDown={stop} role="presentation">
      {medias.map((url, position) => (
        <button
          aria-label={`Agrandir le visuel ${position + 1}${medias.length > 1 ? ` sur ${medias.length}` : ''}`}
          className="group relative shrink-0 cursor-zoom-in rounded-lg transition-transform outline-none hover:scale-[1.03] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:scale-100"
          key={url}
          onClick={() => setIndex(position)}
          style={{ width: size, height: size }}
          type="button"
        >
          <MediaPreview alt="" url={url} variant="thumbnail" />
          <span
            aria-hidden
            className="absolute right-1 bottom-1 flex size-6 items-center justify-center rounded-md bg-background/85 text-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <Maximize2 className="size-3.5" />
          </span>
        </button>
      ))}
      <MediaViewerDialog index={index} medias={medias} onIndexChange={setIndex} title={title} />
    </div>
  );
};
